import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { Icon, type IconName } from '@/components/Icon';
import { getRuntimeConfig } from '@/runtimeConfig';
import type { Alert, WorkflowSummary } from '@/types/sturm';

interface SidebarProps {
  workflows: WorkflowSummary[];
  alerts: Alert[];
}

export function Sidebar({ workflows, alerts }: SidebarProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeQueue = searchParams.get('queue');
  const firing = alerts.filter((a) => a.state === 'firing').length;
  const enabled = workflows.filter((w) => !w.isDisabled);
  const disabled = workflows.length - enabled.length;
  const queues = [...new Set(enabled.map((w) => w.queue))];
  const workers = workflows.reduce((total, w) => total + w.workers, 0);
  const werkUrl = getRuntimeConfig().werkUrl;

  const items: { to: string; label: string; icon: IconName; badge?: number; alarm?: boolean }[] = [
    { to: '/workflows', label: 'Workflows', icon: 'flow', badge: enabled.length },
    { to: '/runs', label: 'All runs', icon: 'history' },
    { to: '/notifications', label: 'Notifications', icon: 'bell', badge: firing, alarm: true },
    { to: '/settings', label: 'Settings', icon: 'settings', badge: disabled > 0 ? disabled : undefined },
  ];

  const infrastructure: { to: string; label: string; icon: IconName }[] = [
    { to: '/inspect/nats', label: 'NATS overview', icon: 'stream' },
    { to: '/inspect/redis', label: 'Redis overview', icon: 'database' },
  ];

  const isWorkflowsActive =
    location.pathname.startsWith('/workflows') && !activeQueue;

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">s</div>
        <div>
          <div className="brand-name">Sturm</div>
          <div className="brand-sub">Workflow orchestration</div>
        </div>
      </div>

      <div className="nav-section-label">Workspace</div>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `nav-item ${(item.to === '/workflows' ? isWorkflowsActive : isActive) ? 'active' : ''}`
          }
        >
          <Icon name={item.icon} size={16} />
          <span className="flex1">{item.label}</span>
          {item.badge != null ? (
            <span
              className="badge"
              style={item.alarm && item.badge ? { color: 'var(--error)', fontWeight: 600 } : undefined}
            >
              {item.badge}
            </span>
          ) : null}
        </NavLink>
      ))}

      <div className="nav-section-label">Infrastructure</div>
      {infrastructure.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <Icon name={item.icon} size={16} />
          <span className="flex1">{item.label}</span>
        </NavLink>
      ))}

      {queues.length > 0 ? <div className="nav-section-label">Task queues</div> : null}
      {queues.map((queue) => (
        <NavLink
          key={queue}
          to={`/workflows?queue=${encodeURIComponent(queue)}`}
          className={`nav-item ${activeQueue === queue ? 'active' : ''}`}
        >
          <Icon name="layers" size={16} />
          <span className="flex1 mono" style={{ fontSize: 12.5 }}>
            {queue}
          </span>
          <span className="badge">{enabled.filter((w) => w.queue === queue).length}</span>
        </NavLink>
      ))}

      <div className="nav-spacer" />

      {werkUrl ? (
        <a className="nav-item" href={werkUrl} style={{ fontSize: 12.5 }}>
          <Icon name="cube" size={15} />
          <span className="flex1">Werk build &amp; deploy</span>
          <span className="badge">
            <Icon name="external" size={11} />
          </span>
        </a>
      ) : null}

      <div className="nav-footer">
        <span className="dot" />
        <span>
          {workers} worker{workers === 1 ? '' : 's'} connected
        </span>
      </div>
    </aside>
  );
}
